<form
  class="create-shipment-modal"
  [formGroup]="form"
  (ngSubmit)="submitForm()"
  novalidate
>
  <section class="create-shipment-modal__panel">
    <div class="create-shipment-modal__grid">
      <div class="create-shipment-modal__field create-shipment-modal__field--span2">
        <label class="form-label" for="create-shipment-order">
          {{ "COMPONENTS.SHIPMENTS.FIELDS.ORDER" | translate }}
        </label>
        <app-search
          id="create-shipment-order"
          customClass="form-search-input"
          formControlName="salesOrderId"
          [items]="(salesOrderOptions$ | async) ?? []"
          [filterFn]="salesOrderFilterFn"
          [formatter]="salesOrderFormatter"
          [compareFn]="salesOrderCompareFn"
          [placeholderText]="'COMPONENTS.SHIPMENTS.PLACEHOLDERS.ORDER' | translate"
        ></app-search>
        @if (form.controls.customerId.touched && form.controls.customerId.hasError('required')) {
          <p class="create-shipment-modal__field-error" role="alert">
            {{ "COMPONENTS.SHIPMENTS.MESSAGES.ORDER_REQUIRED" | translate }}
          </p>
        }
      </div>

      <div class="create-shipment-modal__field">
        <label class="form-label" for="create-shipment-date">
          {{ "COMPONENTS.SHIPMENTS.FIELDS.DATE" | translate }}
        </label>
        <input
          id="create-shipment-date"
          type="date"
          class="form-input"
          formControlName="shipmentDate"
          [class.is-invalid]="form.controls.shipmentDate.touched && form.controls.shipmentDate.invalid"
        />
        @if (form.controls.shipmentDate.touched && form.controls.shipmentDate.hasError('required')) {
          <p class="create-shipment-modal__field-error" role="alert">
            {{ "ERRORS.REQUIRED" | translate }}
          </p>
        }
      </div>
    </div>

    @if (canPrefillFromOrder$ | async) {
      <button
        type="button"
        class="form-secondary-btn create-shipment-modal__prefill"
        (click)="prefillFromOrder()"
      >
        {{ "COMPONENTS.SHIPMENTS.BUTTONS.PREFILL_FROM_ORDER" | translate }}
      </button>
    }
  </section>

  <section class="create-shipment-modal__panel create-shipment-modal__panel--lines">
    <div class="create-shipment-modal__panel-header">
      <h3 class="create-shipment-modal__panel-title">
        {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.ITEM" | translate }}
      </h3>
      <button type="button" class="form-secondary-btn" (click)="addLine()">
        + {{ "COMPONENTS.SHIPMENTS.BUTTONS.ADD_LINE" | translate }}
      </button>
    </div>

    @if (lineForms.length === 0) {
      <p class="create-shipment-modal__empty">
        {{ "COMPONENTS.SHIPMENTS.MESSAGES.NO_LINES" | translate }}
      </p>
    } @else {
      <div class="create-shipment-modal__lines" formArrayName="lines">
        @for (lineForm of lineForms.controls; track $index) {
          <article class="create-shipment-modal__line" [formGroupName]="$index">
            <header class="create-shipment-modal__line-header">
              <span class="create-shipment-modal__line-title">
                #{{ $index + 1 }}
              </span>
              <button
                type="button"
                class="create-shipment-modal__line-remove"
                (click)="removeLine($index)"
              >
                {{ "COMPONENTS.SHIPMENTS.BUTTONS.REMOVE_LINE" | translate }}
              </button>
            </header>

            <div class="create-shipment-modal__line-grid">
              <div class="create-shipment-modal__field create-shipment-modal__field--span2">
                <label class="form-label" [attr.for]="'create-shipment-item-' + $index">
                  {{ "COMPONENTS.SHIPMENTS.FIELDS.ITEM" | translate }}
                </label>
                <app-search
                  [id]="'create-shipment-item-' + $index"
                  customClass="form-search-input"
                  formControlName="inventoryItemId"
                  [items]="(inventoryItemOptions$ | async) ?? []"
                  [filterFn]="inventoryItemFilterFn"
                  [formatter]="inventoryItemFormatter"
                  [compareFn]="inventoryItemCompareFn"
                  [placeholderText]="'COMPONENTS.SHIPMENTS.PLACEHOLDERS.ITEM' | translate"
                ></app-search>
              </div>

              @if (lineForm.controls.isServiceLine.value) {
                <div class="create-shipment-modal__field create-shipment-modal__field--service-note">
                  <span class="create-shipment-modal__service-badge">
                    {{ "COMPONENTS.SALES_ORDERS.LINES.SERVICE_BADGE" | translate }}
                  </span>
                  <span class="create-shipment-modal__service-hint">
                    {{ "COMPONENTS.SALES_ORDERS.AVAILABILITY.ALWAYS_AVAILABLE" | translate }}
                  </span>
                </div>
              } @else {
                <div class="create-shipment-modal__field">
                  <label class="form-label" [attr.for]="'create-shipment-warehouse-' + $index">
                    {{ "COMPONENTS.SHIPMENTS.FIELDS.WAREHOUSE" | translate }}
                  </label>
                  <input
                    [id]="'create-shipment-warehouse-' + $index"
                    type="text"
                    class="form-input"
                    formControlName="warehouseId"
                    [placeholder]="'COMPONENTS.SHIPMENTS.PLACEHOLDERS.WAREHOUSE' | translate"
                    [class.is-invalid]="lineForm.controls.warehouseId.touched && lineForm.controls.warehouseId.invalid"
                  />
                </div>
              }

              <div class="create-shipment-modal__field">
                <label class="form-label" [attr.for]="'create-shipment-qty-' + $index">
                  {{ "COMPONENTS.SHIPMENTS.FIELDS.QUANTITY" | translate }}
                </label>
                <input
                  [id]="'create-shipment-qty-' + $index"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  class="form-input"
                  formControlName="quantity"
                  [class.is-invalid]="lineForm.controls.quantity.touched && lineForm.controls.quantity.invalid"
                />
              </div>

              <div class="create-shipment-modal__field">
                <label class="form-label" [attr.for]="'create-shipment-uom-' + $index">
                  {{ "COMPONENTS.SHIPMENTS.FIELDS.UNIT" | translate }}
                </label>
                <select
                  [id]="'create-shipment-uom-' + $index"
                  class="form-input"
                  formControlName="unitOfMeasureId"
                  [class.is-invalid]="lineForm.controls.unitOfMeasureId.touched && lineForm.controls.unitOfMeasureId.invalid"
                >
                  @for (unit of unitsOfMeasure$ | async; track unit.id) {
                    <option [ngValue]="unit.id">{{ unit.displayName || unit.code }}</option>
                  }
                </select>
              </div>
            </div>
          </article>
        }
      </div>
    }
  </section>

  @if (errorMessageKey) {
    <p class="create-shipment-modal__error" role="alert">
      {{ errorMessageKey | translate }}
    </p>
  }

  <div class="create-shipment-modal__actions">
    <button type="button" class="form-secondary-btn" [disabled]="isSubmitting" (click)="onCancel()">
      {{ "BUTTONS.CANCEL" | translate }}
    </button>

    <button
      type="submit"
      class="form-primary-btn"
      [disabled]="isSubmitting || form.invalid || lineForms.length === 0"
    >
      @if (isSubmitting) {
        {{ "COMPONENTS.SHIPMENTS.BUTTONS.SUBMITTING" | translate }}
      } @else {
        {{ "COMPONENTS.SHIPMENTS.BUTTONS.SUBMIT" | translate }}
      }
    </button>
  </div>
</form>
